'use client';

import { useMemo } from "react";
import { SimilarPostCard } from './post-cards';
import { usePosts } from './posts.provider';

interface SimilarPostsProps {
    currentSlug: string;
    maxPosts?: number;
}

// Fonction de mélange (Fisher-Yates shuffle algorithm)
function shuffleArray<T>(array: T[]): T[] {
    const newArray = [...array]; // Créer une copie pour ne pas modifier l'original
    for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]]; // Échange
    }
    return newArray;
}

export function SimilarPosts({ currentSlug, maxPosts = 3 }: SimilarPostsProps) {
    const { posts, setCurrentPost } = usePosts();

    // Filtrer et mélanger les articles (en excluant l'article courant)
    const shuffledSimilarPosts = useMemo(() => {
        // Filtrer pour exclure l'article courant
        const filteredPosts = posts.filter(post => post.slug !== currentSlug);

        // Mélanger les articles
        const shuffled = shuffleArray(filteredPosts);

        // Limiter au nombre maximal d'articles à afficher
        return shuffled.slice(0, maxPosts);
    }, [posts, currentSlug, maxPosts]);

    // Ne rien afficher s'il n'y a pas d'articles similaires
    if (shuffledSimilarPosts.length === 0) {
        return null;
    }

    return (
        <div className="mt-16 pt-8 border-t border-gray-200">
            <h2 className="text-2xl font-bold font-exo text-bleu-marine mb-6">Articles similaires</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {shuffledSimilarPosts.map((post) => (
                    <SimilarPostCard
                        key={post.slug}
                        post={post}
                        setCurrentPost={setCurrentPost}
                    />
                ))}
            </div>
        </div>
    );
}
